import { useRef } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogMain,
  DialogFooter,
} from "@ncoder/ui";
import { cn } from "@/lib/utils";
import { Button } from "@/components/atoms/multi-agent/Button";
import {
  FEEDBACK_REASONS,
  FEEDBACK_COMMENT_MAX_LENGTH,
} from "@/constants/multi-agent/feedback.constants";
import type { UseBadResponseFeedbackReturn } from "@/hooks/multi-agent/useBadResponseFeedback";

interface BadResponsePopoverProps {
  /** The trigger element — typically the ThumbsDown IconButton. */
  children: React.ReactElement;
  feedback: UseBadResponseFeedbackReturn;
}

export function BadResponsePopover({ children, feedback }: BadResponsePopoverProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const {
    open,
    selectedReasons,
    comment,
    submitting,
    error,
    openDialog,
    closeDialog,
    toggleReason,
    setComment,
    submit,
    clearError,
  } = feedback;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => (isOpen ? openDialog() : closeDialog())}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>

      <DialogContent
        className="w-full max-w-[560px]"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          textareaRef.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>What went wrong?</DialogTitle>
        </DialogHeader>

        <DialogMain>
          <div className="space-y-4">
            {/* Reason chips — multi-select */}
            <div
              className="flex flex-wrap gap-1.5"
              role="group"
              aria-label="Select one or more reasons"
            >
              {FEEDBACK_REASONS.map((reason) => {
                const active = selectedReasons.has(reason.id);
                return (
                  <button
                    key={reason.id}
                    type="button"
                    onClick={() => toggleReason(reason.id)}
                    aria-pressed={active}
                    className={cn(
                      "inline-flex cursor-pointer select-none items-center rounded-full border px-2.5 py-1",
                      "text-xs font-medium transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                      active
                        ? "border-primary/50 bg-primary/10 text-primary"
                        : "border-border bg-muted/40 text-muted-foreground hover:border-primary/30 hover:bg-muted/60 hover:text-foreground",
                    )}
                  >
                    {reason.label}
                  </button>
                );
              })}
            </div>

            {/* Comment textarea */}
            <textarea
              ref={textareaRef}
              value={comment}
              onChange={(e) => {
                setComment(e.target.value);
                if (error) clearError();
              }}
              placeholder="Tell us what went wrong (optional)"
              maxLength={FEEDBACK_COMMENT_MAX_LENGTH}
              rows={4}
              className={cn(
                "w-full resize-none rounded-md border border-input bg-background",
                "px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                "min-h-[80px] transition-shadow",
              )}
            />

            {/* Inline error */}
            {error && (
              <p className="text-xs text-destructive" role="alert" aria-live="assertive">
                {error}{" "}
                <button
                  type="button"
                  onClick={clearError}
                  className="underline hover:no-underline"
                >
                  Dismiss
                </button>
              </p>
            )}
          </div>
        </DialogMain>

        <DialogFooter>
          <Button
            variant="ghost"
            size="sm"
            onClick={closeDialog}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={submit}
            busy={submitting}
            disabled={submitting}
            aria-label="Submit feedback"
          >
            {submitting ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin" />
                Sending…
              </>
            ) : (
              "Send feedback"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
